import { Component, inject, Injector, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RuleInterface } from '@vendasta/automata';
import { AutomationVariableMenuComponent } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.component';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { CrmFieldOptionsSelectorService } from '../../../../../data-services/crm-field-options-selector.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { ActivityDirection, ActivityOutcome, CallStatus, StandardIds } from '@galaxy/crm/static';
import { StaticSelectorService } from '../../../../../data-services/static-selector.service';
import { PersonaType } from '@vendasta/iam';
import { UserSelectorService } from '../../../../../data-services/user-selector.service';

const callDirectionChoices = new Map<string, string>([
  [ActivityDirection.Outbound, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.OUTBOUND'],
  [ActivityDirection.Inbound, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.INBOUND'],
]);

const callStatusChoices = new Map<string, string>([
  [ActivityOutcome.Demo, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.DEMO'],
  [ActivityOutcome.Interested, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.INTERESTED'],
  [ActivityOutcome.NoInterest, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.NO_INTEREST'],
  [ActivityOutcome.QualityConnect, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.QUALITY_CONNECT'],
  [ActivityOutcome.Rescheduling, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.RESCHEDULING'],
  [ActivityOutcome.Referred, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.REFERRED'],
  [ActivityOutcome.Timeline, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.TIMELINE'],
]);

const callOutcomeChoices = new Map<string, string>([
  [CallStatus.Connected, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.CONNECTED'],
  [CallStatus.Busy, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.BUSY'],
  [CallStatus.Rejected, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.REJECTED'],
  [CallStatus.Voicemail, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.VOICEMAIL'],
  [CallStatus.NoAnswer, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.NO_ANSWER'],
  [CallStatus.NotInService, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.NOT_IN_SERVICE'],
  [CallStatus.WrongNumber, 'AUTOMATIONS.EDITOR.TASKS.CREATE_CALL_ACTIVITY.WRONG_NUMBER'],
]);

@Component({
  selector: 'automata-create-crm-call-activity-panel',
  templateUrl: './create-crm-call-activity-panel.component.html',
  styleUrls: ['./create-crm-call-activity-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationVariableMenuComponent,
    AutomationReplacementChipsComponent,
  ],
})
export class CreateCrmCallActivityPanelComponent extends CommonActionPanelComponent implements OnInit {
  tagsService = inject(CrmFieldOptionsSelectorService);
  public activityTags = StandardIds.ActivityTags;
  public callDirectionService = new StaticSelectorService(callDirectionChoices, this.translateService);
  public callStatusService = new StaticSelectorService(callStatusChoices, this.translateService);
  public callOutcomeService = new StaticSelectorService(callOutcomeChoices, this.translateService);
  public userSelectorService = this.injector.get(UserSelectorService);

  personas = [PersonaType.partner, PersonaType.sales_person];

  form: UntypedFormGroup = new UntypedFormGroup({
    body: new FormControl<string>('', Validators.required),
    tags: new FormControl<string[]>([]),
    callDirection: new FormControl<string | null>(null),
    callOutcome: new FormControl<string | null>(null),
    callStatus: new FormControl<string | null>(null),
    callRecordingUrl: new FormControl<string>(''),
    ownerID: new FormControl<string | null>(null),
  });

  constructor(private injector: Injector) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.form.controls.callDirection.setValue(jsonData?.call_direction || null);
    this.form.controls.body.setValue(jsonData?.call_body || '');
    this.form.controls.callStatus.setValue(jsonData?.call_status || null);
    this.form.controls.callOutcome.setValue(jsonData?.call_outcome || null);
    this.form.controls.callRecordingUrl.setValue(jsonData?.call_recording_url || '');
    this.form.controls.tags.setValue(jsonData?.tags || []);
    this.form.controls.ownerID.setValue(jsonData?.owner_id || null);
  }

  finishedLoadingSelector(): void {
    this.savingEnabled = true;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    jsonData.call_direction = this.form.controls.callDirection.value;
    jsonData.call_body = this.form.controls.body.value;
    jsonData.call_status = this.form.controls.callStatus.value;
    jsonData.call_outcome = this.form.controls.callOutcome.value;
    jsonData.call_recording_url = this.form.controls.callRecordingUrl.value;
    jsonData.tags = this.form.controls.tags.value;
    jsonData.owner_id = this.form.controls.ownerID.value;

    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  insertVariable(elementID: string, control: AbstractControl, variable: string): void {
    insertVariable(elementID, control, variable);
  }
}
